import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.addEventListener('change', this.onChange.bind(this));
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        // Ensure checkbox is enabled immediately
        if (this.checkboxInput) {
          this.checkboxInput.removeAttribute('disabled');
          this.checkboxInput.disabled = false;
        }
        
        // Enable checkbox immediately when connected
        this.enableCheckbox();
        
        // Also ensure it's enabled after a brief delay to catch any race conditions
        setTimeout(() => {
          if (this.checkboxInput) {
            this.checkboxInput.removeAttribute('disabled');
            this.checkboxInput.disabled = false;
          }
          this.enableCheckbox();
        }, 100);
        
        // Listen for cart updates to reset form after successful addition
        this.cartUpdateUnsubscriber = () => {
          document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
        };
        document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));

        // Listen for cart errors to display validation errors
        this.cartErrorUnsubscriber = () => {
          document.removeEventListener(ThemeEvents.cartError, this.handleCartError);
        };
        document.addEventListener(ThemeEvents.cartError, this.handleCartError.bind(this));

        // Listen for variant changes
        const target = this.closest('.shopify-section, dialog, product-card');
        if (target) {
          this.variantChangeUnsubscriber = () => {
            target.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantChange);
          };
          target.addEventListener(ThemeEvents.variantUpdate, this.handleVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      handleCartUpdate = (event) => {
        // Check if this cart update is from the product form for this variant
        // CartAddEvent includes productId in data, and variantId might be in the sourceId
        const productId = event.detail?.data?.productId;
        const variantId = event.detail?.data?.variantId || event.detail?.sourceId;
        const source = event.detail?.data?.source;
        
        if (
          source === 'product-form-component' &&
          productId &&
          (variantId?.toString() === this.currentProductVariantId || 
           // Fallback: check if the form's product matches
           this.closest('product-form-component')?.dataset?.productId === productId)
        ) {
          // Only reset if cart update was successful (not an error)
          if (!event.detail?.data?.didError) {
            this.resetRecipientForm();
          }
        }
      };

      handleCartError = (event) => {
        // Check if this error is from the product form for this variant
        // CartErrorEvent includes sourceId and message in data
        const sourceId = event.detail?.sourceId;
        const productForm = this.closest('product-form-component');
        
        if (
          sourceId &&
          productForm &&
          productForm.id === sourceId
        ) {
          // Parse error message and display in form
          const message = event.detail?.data?.message;
          const errors = event.detail?.data?.errors;
          
          if (errors) {
            this.displayErrorMessage(message || this.defaultErrorHeader, errors);
          } else if (message) {
            this.displayErrorMessage(message);
          }
        }
      };

      enableCheckbox() {
        // Enable checkbox once element is connected to DOM
        if (this.checkboxInput) {
          // Force enable checkbox - remove any disabled state
          this.checkboxInput.removeAttribute('disabled');
          this.checkboxInput.disabled = false;
          this.checkboxInput.readOnly = false;
          this.checkboxInput.setAttribute('aria-disabled', 'false');
          
          // Force enable with inline styles
          this.checkboxInput.style.pointerEvents = 'auto';
          this.checkboxInput.style.cursor = 'pointer';
          this.checkboxInput.style.opacity = '0';
          this.checkboxInput.style.position = 'absolute';
          this.checkboxInput.style.zIndex = '10';
          
          // Ensure it starts unchecked
          this.checkboxInput.checked = false;
          
          // Add direct click listener to checkbox
          this.checkboxInput.addEventListener('click', (e) => {
            e.stopPropagation();
            this.onChange();
          });
        }
        
        // Get the label and make entire area clickable
        const checkboxLabel = this.querySelector(`label[for="Recipient-checkbox-${this.dataset.sectionId}"]`);
        if (checkboxLabel && this.checkboxInput) {
          checkboxLabel.style.cursor = 'pointer';
          checkboxLabel.style.pointerEvents = 'auto';
          checkboxLabel.style.userSelect = 'none';
          
          // Add click handler that ensures checkbox toggles when clicking anywhere on label
          checkboxLabel.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Ensure checkbox is enabled
            if (this.checkboxInput) {
              this.checkboxInput.removeAttribute('disabled');
              this.checkboxInput.disabled = false;
              
              // Manually toggle the checkbox
              this.checkboxInput.checked = !this.checkboxInput.checked;
              
              // Trigger change event
              this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
              
              // Trigger onChange to show/hide form
              this.onChange();
            }
          }, { capture: false });
        }
        
        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = true;
        }

        // Hide form fields initially
        this.disableInputFields();
        
        // Call onChange to set initial state
        this.onChange();
      }

      handleVariantChange = (event) => {
        // Update current variant ID when variant changes
        // This is important for gift cards with multiple variants (different amounts)
        if (event.detail?.resource?.id) {
          this.currentProductVariantId = event.detail.resource.id.toString();
          
          // Update the data attribute on the recipient-form element
          if (this.dataset) {
            this.dataset.productVariantId = this.currentProductVariantId;
          }
        }
      };

      onChange() {
        if (this.checkboxInput?.checked) {
          this.enableInputFields();
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = 'Gift card recipient form expanded';
          }
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          if (this.recipientFieldsLiveRegion) {
            this.recipientFieldsLiveRegion.innerText = 'Gift card recipient form collapsed';
          }
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          if (field) field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = false;
        });
        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = false;
        }
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = true;
        });
        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = true;
        }
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.hidden = false;

        if (typeof body === 'object') {
          if (this.errorMessage) {
            this.errorMessage.innerText = this.defaultErrorHeader || title;
          }

          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = Array.isArray(value) ? value.join(', ') : String(value);
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);

            if (!errorMessageElement) return;

            const errorTextElement = errorMessageElement.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            // Map key names to input elements
            const keyMap = {
              email: 'emailInput',
              name: 'nameInput',
              message: 'messageInput',
              send_on: 'sendonInput',
            };

            const inputElement = this[keyMap[key]];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', 'true');
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        if (this.errorMessage) {
          this.errorMessage.innerText = body || title;
        }
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) {
          this.errorMessageList.innerHTML = '';
        }

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        this.inputFields().forEach((inputElement) => {
          if (inputElement) {
            inputElement.setAttribute('aria-invalid', 'false');
            inputElement.removeAttribute('aria-describedby');
          }
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput?.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
          this.onChange();
        }
      }
    }
  );
}

